import { useCallback, useEffect, useRef } from 'react';
import { getEventFeed } from '../../Services/arndAPI';

const POLL_MS = 30000;

/**
 * Polls the purchase-only feed every 30s while the tab is visible and calls
 * `onPurchase(row)` for each purchase a poll turns up. The initial load
 * never calls `onPurchase` — there is no "previous" newest to compare it
 * against, so every row on the first response is old news, not a live
 * event. This is what lets EventOps.jsx ring the register (SCRUM-356)
 * without any dedicated purchases UI to poll from.
 *
 * Returns `pollNow()` so a manual Refresh or a send can check right away
 * (SCRUM-419). Polls never overlap: a kick that lands while the interval's
 * request is in flight is dropped, because both would carry the same
 * `since` and hand the same purchase to `onPurchase` twice.
 */
export default function usePurchasePoll(eventId, onPurchase) {
  const newestRef = useRef(null);
  // Tracks whether the FIRST fetch has completed — distinct from
  // `newestRef` being non-null, because an event with no purchases yet
  // keeps `newest` at null forever. Using `newestRef == null` as the
  // "was this the initial load" signal made every tick until the first
  // sale look like the initial load, so that first-ever purchase never
  // rang the register.
  const hasFetchedRef = useRef(false);
  const inFlightRef = useRef(false);
  const onPurchaseRef = useRef(onPurchase);
  onPurchaseRef.current = onPurchase;

  const pollNow = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const data = await getEventFeed(eventId, {
        kind: 'purchase',
        since: newestRef.current ?? undefined,
      });
      const isInitial = !hasFetchedRef.current;
      hasFetchedRef.current = true;
      if (data?.newest) newestRef.current = data.newest;
      if (!isInitial) {
        for (const row of data?.rows ?? []) onPurchaseRef.current?.(row);
      }
    } catch {
      /* best-effort — a missed tick just gets caught on the next poll */
    } finally {
      inFlightRef.current = false;
    }
  }, [eventId]);

  useEffect(() => {
    newestRef.current = null;
    hasFetchedRef.current = false;

    pollNow(); // establish a baseline `newest` immediately rather than waiting 30s
    const t = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      pollNow();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [pollNow]);

  return pollNow;
}
